import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import RewardsIcon from './svg/RewardsIcon';
import Tooltip from './common/Tooltip';
import InfoIcon from '../assets/images/info.svg';

/**
 * RewardsKaminoCard
 * ------------------
 * Card informativa (read-only fase A) que muestra el APY en vivo
 * que paga Kamino Lend sobre USDC, con el fee de Flowex ya descontado.
 *
 * Endpoints:
 *   GET /rewards/kamino/apy          — APY actual
 *   GET /rewards/kamino/apy-history  — sparkline 30d
 *
 * Fallback: si el backend aún no responde (RPC caído o SDK no instalado),
 * la card muestra "Próximamente" en vez de romper el Dashboard.
 */
const RewardsKaminoCard = () => {
  const [apy, setApy] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [apyRes, histRes] = await Promise.all([
          API.get('/rewards/kamino/apy'),
          API.get('/rewards/kamino/apy-history?days=30'),
        ]);
        setApy(apyRes.data.data);
        setHistory(histRes.data.data || []);
      } catch (e) {
        console.warn('Kamino APY no disponible:', e?.message);
        setError(true);
      }
    })();
  }, []);

  // Sparkline SVG simple (sin dep). Normaliza y dibuja un polyline.
  const renderSparkline = () => {
    if (!history || history.length < 2) return null;
    const values = history.map((h) => h.net_supply_apy);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const W = 200;
    const H = 40;
    const points = values
      .map((v, i) => {
        const x = (i / (values.length - 1)) * W;
        const y = H - ((v - min) / range) * H;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
    return (
      <svg width={W} height={H} className="mt-2">
        <polyline
          fill="none"
          stroke="#2A59C6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>
    );
  };

  return (
    <div className="w-full p-6 bg-white rounded-[20px] shadow-xs border-1 border-[#E6EBF7] space-y-2">
      <div className="flex items-center justify-start space-x-1">
        <div className="w-[22px] h-[22px] bg-[#FEF0C7] flex items-center justify-center rounded-full">
          <RewardsIcon color="#F79009" size="w-[14px] h-[14px]" />
        </div>
        <p className="text-[#414651] text-base font-semibold pr-1">Yield on-chain (USDC)</p>
        <div className="relative group">
          <img src={InfoIcon} alt="Info Icon" className="w-5 h-5 cursor-pointer" />
          <Tooltip text="APY variable generado por el protocolo descentralizado Kamino en Solana. FlowexPay no custodia depósitos ni garantiza rendimientos." />
        </div>
      </div>

      {error || !apy ? (
        <div className="p-6 bg-[#F9FAFB] rounded-[20px] text-center">
          <p className="text-sm text-[#717680] font-normal">
            {error
              ? 'Tasa no disponible en este momento'
              : 'Cargando tasa on-chain...'}
          </p>
        </div>
      ) : (
        <div className="p-6 bg-[#F9FAFB] rounded-[20px] space-y-1">
          <p className="text-sm text-[#181D27] font-normal">APY actual para ti</p>
          <p className="text-[22px] font-semibold text-[#2A59C6]">
            {apy.net_supply_apy.toFixed(2)}
            <span className="text-lg"> % anual</span>
          </p>
          {renderSparkline()}
        </div>
      )}
    </div>
  );
};

export default RewardsKaminoCard;
